/**
 * 🐙 PulpinWidget — Fase 1
 * Botón flotante que aparece en todas las páginas autenticadas.
 * Abre un mini-panel con enlace a WhatsApp inteligente y preview del último tip.
 */

import { useState, useEffect } from 'react'
import { X, MessageCircle, BookOpen, ExternalLink } from 'lucide-react'
import { useAuthStore } from '../../hooks/useAuth'
import api from '../../lib/api'

interface UltimoPost {
  id: number
  titulo: string
  emoji: string
  tipo: string
}

// Número de WhatsApp de Pulpín (el bot)
const PULPIN_WA = '573157260804'

function buildWhatsAppLink(userName?: string, userSede?: string): string {
  const saludo = userName ? `Hola Pulpín, soy ${userName}` : 'Hola Pulpín'
  const sede = userSede ? ` de la sede ${userSede}` : ''
  const mensaje = encodeURIComponent(`${saludo}${sede}, tengo una pregunta sobre acuarismo.`)
  return `https://wa.me/${PULPIN_WA}?text=${mensaje}`
}

export default function PulpinWidget() {
  const user = useAuthStore(s => s.user)
  const [open, setOpen] = useState(false)
  const [ultimoPost, setUltimoPost] = useState<UltimoPost | null>(null)
  const [pulse, setPulse] = useState(true)

  useEffect(() => {
    // Cargar el último post publicado de Pulpín
    api.get('/pulpin/posts', { params: {} })
      .then(r => {
        if (r.data.length > 0) setUltimoPost(r.data[0])
      })
      .catch(() => {})

    // Quitar pulso después de 5s para no molestar
    const t = setTimeout(() => setPulse(false), 5000)
    return () => clearTimeout(t)
  }, [])

  const waLink = buildWhatsAppLink(user?.nombre, user?.sede ?? undefined)

  return (
    <>
      {/* Botón flotante — sube sobre el nav en móvil */}
      <button
        onClick={() => { setOpen(v => !v); setPulse(false) }}
        className="fixed bottom-20 lg:bottom-5 right-4 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-aqua-500 to-violet-600 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center overflow-hidden"
        title="Hablar con Pulpín"
        aria-label="Pulpín asistente"
      >
        <img src="/pulpin-chat.png" alt="Pulpín"
          className="w-12 h-12 object-contain scale-110"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; e.currentTarget.insertAdjacentHTML('afterend','<span class="text-2xl">🐙</span>') }}
        />
        {pulse && (
          <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-green-400 border-2 border-white animate-pulse" />
        )}
      </button>

      {/* Panel — también sube en móvil */}
      {open && (
        <div className="fixed bottom-36 lg:bottom-22 right-4 z-50 w-80 rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-aqua-500 to-violet-600 px-4 py-3 flex items-center gap-3">
            <img src="/pulpin-chat.png" alt="Pulpín" className="w-8 h-8 object-contain"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; e.currentTarget.insertAdjacentHTML('afterend','<span class="text-2xl">🐙</span>') }}
            />
            <div className="flex-1">
              <div className="font-extrabold text-white text-sm">Pulpín</div>
              <div className="text-white/70 text-xs">Experto en acuarismo · En línea</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            {/* Saludo personalizado */}
            <div className="bg-aqua-50 rounded-xl p-3">
              <p className="text-sm text-gray-700 leading-snug">
                ¡Hola{user?.nombre ? `, <strong>${user.nombre.split(' ')[0]}</strong>` : ''}! 👋
                Soy Pulpín, tu guía de acuarismo. ¿En qué te puedo ayudar hoy?
              </p>
            </div>

            {/* Último post si hay */}
            {ultimoPost && (
              <a
                href={`/cliente/pulpin`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl border border-aqua-100 hover:border-aqua-300 hover:bg-aqua-50 transition-all group"
              >
                <span className="text-2xl">{ultimoPost.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-aqua-500 font-bold mb-0.5 flex items-center gap-1">
                    <BookOpen size={10} /> Último post
                  </div>
                  <div className="text-xs text-gray-700 font-semibold leading-snug line-clamp-2">
                    {ultimoPost.titulo}
                  </div>
                </div>
                <ExternalLink size={13} className="text-gray-300 group-hover:text-aqua-400 flex-shrink-0 transition-colors" />
              </a>
            )}

            {/* Botón WhatsApp */}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full py-3 rounded-xl font-bold text-sm text-white transition-all"
              style={{ background: '#25D366' }}
              onClick={() => setOpen(false)}
            >
              <MessageCircle size={18} />
              Chatear con Pulpín por WhatsApp
            </a>

            {/* Link a feed completo */}
            <a
              href="/cliente/pulpin"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm text-aqua-600 border border-aqua-200 hover:bg-aqua-50 transition-all"
            >
              <BookOpen size={15} /> Ver todos los tips de Pulpín
            </a>
          </div>

          {/* Footer */}
          <div className="px-4 pb-3 text-center">
            <p className="text-xs text-gray-300">
              Pulpín responde en WhatsApp las 24h 🕐
            </p>
          </div>
        </div>
      )}
    </>
  )
}
